import React from 'react';
import Sidebar from '../../../views/components/Sidebar';
import LogoMin from '../../../images/svg/logoMin';
import { Divider, List } from '@mui/material';
import HeaderWithLogo from '../../../views/components/Sidebar/HeaderWithLogo';
import LegacyNavItem from '../../../views/components/Sidebar/LegacyNavItem';
import ControllerItem from '../../../views/components/Sidebar/ControllerItem';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../redux-file/hooks';
import ExpandableListItem from '../../../views/components/Sidebar/ExpandableItemList';
import { useGhgOrganization } from '../../../common/hooks';
import { OrganizationTag, hasTag } from '../../../utils/tags';
import { HelpCenterNavItem } from '../../../common/sidebar';
import {
  useGhgSidebarPermissions,
  useSupplierSurveyOwn,
  useYearScope12Completion,
} from 'src/Ghg/hooks';
import GhgSelectedYear from './GhgSelectedYear';
import LogoWithRaben from './components/logoWithRaben';
import GhgScope3Items from './GhgScope3Items';
import DownloadReportItem from './DownloadReportItem';
import DownloadCertificateItem from './DownloadCertificateItem';

export default function GhgOrganizationSidebar() {
  const { t } = useTranslation();
  const organization = useGhgOrganization();
  const showScope3 = !hasTag(organization, OrganizationTag.HideScope3);
  const canWriteData = organization.can_read_data;

  const { selectedDate } = useAppSelector((state) => state.questionnaire);
  const selectedYear = String(selectedDate.getFullYear());
  const { hasAccess } = useGhgSidebarPermissions(
    organization.id,
    selectedDate.getFullYear()
  );

  const isRabenRoot =
    !!organization && hasTag(organization, OrganizationTag.RabenRoot);
  const { data: surveyData } = useSupplierSurveyOwn(
    organization.id,
    selectedYear
  );
  const surveyFinished = surveyData?.answer?.is_submitted;
  const scope12Unlocked =
    !hasTag(organization, OrganizationTag.Raben) ||
    surveyData?.answer?.wants_to_use_platform === true;

  const { data } = useSupplierSurveyOwn(organization.id, selectedYear);

  const supplierSurveyOwnLink = data?.access_token
    ? `/suppliers/${data.access_token}/main-page?vat_identifier=${organization.vat_identifier}`
    : null;

  // Raben 14.10: the same rule as the certificate step in Clippy (every period
  // of the year; for a Raben carrier Scope 1 also needs data).
  const { scope1Complete, scope2Complete } = useYearScope12Completion(
    organization,
    selectedDate.getFullYear()
  );

  return (
    <Sidebar>
      <HeaderWithLogo openLogo={<LogoWithRaben />} closedLogo={<LogoMin />} />

      <List>
        <LegacyNavItem name="dashboard" path="dashboard" />
        {/* RG 28.08 2.2: second row, under Dashboard. */}
        {canWriteData && hasTag(organization, OrganizationTag.Raben) && (
          <LegacyNavItem
            name="supplier-commitment"
            path="supplier-commitment"
            icon="check"
          />
        )}
        {isRabenRoot && (
          <LegacyNavItem
            name="suppliers-surveys"
            path="emissions/questionnairepage-goods-and-services/products-from-suppliers"
          />
        )}
        {supplierSurveyOwnLink && (
          <LegacyNavItem
            name="suppliers-survey-own"
            path={supplierSurveyOwnLink}
            newTab
            icon="suppliers-surveys"
          />
        )}
        {canWriteData && (
          <>
            <ExpandableListItem
              text={t('menu.emission-data')}
              secondaryAction={<GhgSelectedYear />}
              defaultOpen
            >
              {hasAccess('scope_1', 'direct_emissions') && (
                <LegacyNavItem
                  name="direct-emissions"
                  path="emissions/questionnairepage-direct-emissions"
                  ButtonProps={{ disabled: !scope12Unlocked }}
                />
              )}
              {hasAccess('scope_2', 'electricity_emissions') && (
                <LegacyNavItem
                  name="electricity-emissions"
                  path="emissions/questionnairepage-electricity-emissions"
                  ButtonProps={{ disabled: !scope12Unlocked }}
                />
              )}
              {showScope3 && <GhgScope3Items />}
              <Divider sx={{ m: '10px' }} />
            </ExpandableListItem>

            <LegacyNavItem name="about-company" path="about-company" />
          </>
        )}
        <DownloadReportItem />
        {scope1Complete && scope2Complete && surveyFinished && (
          <DownloadCertificateItem />
        )}
      </List>
      <HelpCenterNavItem />
      {/* <ApiDocsNavItem /> */}
      <ControllerItem />
    </Sidebar>
  );
}
